import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Modal, Text } from '@frontend/design-system';
import { MAX_CALENDAR_LOCATION_COUNT } from '../../constants';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useCalendarLocationShallowStore } from '../../stores/calendar-location.store';
import { useCalendarStore } from '../../stores/calendar.store';
import { LocationEntity } from '../location-entity';
import { AddLocationButton } from './add-location-button';
import { getHelperText, getSelectedLocationsDescription, getSelectedLocationsHeader } from './helpers';

const useCalendarLocationSelectorModalControl = () => {
  const { shouldShowLocationSelectorModal, setShowLocationSelectorModal } = useCalendarStore([
    'shouldShowLocationSelectorModal',
    'setShowLocationSelectorModal',
  ]);

  return {
    modalProps: {
      show: shouldShowLocationSelectorModal,
      onClose: () => setShowLocationSelectorModal(false),
    },
  };
};

type Props = {
  onSelect?: (locationIds: string[]) => void;
};

export const CalendarLocationSelectorModal = ({ onSelect }: Props) => {
  const { t } = useTranslation('schedule');
  const { selectedGroupId, selectedLocationIds: selectedAppLocationIds, getScopeName } = useSchedulingLocationInfo();
  const { selectedLocationIds: globallySelectedLocationIds } = useAppScopeStore();
  const { setShowLocationSelectorModal, resetCalendarFilters } = useCalendarStore([
    'setShowLocationSelectorModal',
    'resetCalendarFilters',
  ]);
  const { calendarLocationMap, setCalendarLocationMap } = useCalendarLocationShallowStore(
    'calendarLocationMap',
    'setCalendarLocationMap'
  );

  const { modalProps } = useCalendarLocationSelectorModalControl();
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  const prevSelectedLocations = calendarLocationMap[selectedGroupId];
  const hasPrevSelectedLocations = !!prevSelectedLocations?.length;

  // open location selector if locations already not set in store
  useEffect(() => {
    const locationIds = calendarLocationMap[selectedGroupId] || [];
    if (!!locationIds.length) return;
    if (!selectedAppLocationIds?.length) return;

    if (selectedAppLocationIds.length > MAX_CALENDAR_LOCATION_COUNT) {
      setShowLocationSelectorModal(true);
    }
  }, [setShowLocationSelectorModal, calendarLocationMap, selectedGroupId, selectedAppLocationIds]);

  // Update `selectedLocationIds` when the modal opens or `calendarLocationMap` changes
  useEffect(() => {
    if (!modalProps.show) return;
    const newSelectedLocationIds = hasPrevSelectedLocations
      ? prevSelectedLocations ?? []
      : selectedAppLocationIds.slice(0, MAX_CALENDAR_LOCATION_COUNT);

    setSelectedLocationIds(newSelectedLocationIds);
  }, [hasPrevSelectedLocations, modalProps.show, prevSelectedLocations, selectedAppLocationIds]);

  // NOTE: Keeping this for V2 case, to ensure backward compatibility
  useEffect(() => {
    if (hasPrevSelectedLocations) {
      onSelect?.(prevSelectedLocations);
    }
  }, [hasPrevSelectedLocations, prevSelectedLocations]);

  const addLocation = (locationId: string) => {
    setSelectedLocationIds((prev) => [...prev, locationId]);
  };
  const removeLocation = (locationId: string) => {
    setSelectedLocationIds(selectedLocationIds.filter((id) => id !== locationId));
  };

  const handleViewButtonClick = () => {
    setCalendarLocationMap(selectedGroupId, selectedLocationIds);
    resetCalendarFilters(selectedLocationIds);
    modalProps.onClose();
  };

  const isModalCloseDisabled = !hasPrevSelectedLocations;
  const isMaxLocationsSelected = selectedLocationIds.length === MAX_CALENDAR_LOCATION_COUNT;
  const unselectedLocationIds = selectedAppLocationIds.filter((id) => !selectedLocationIds.includes(id));

  const modalLocationsSelectedCount = selectedLocationIds.length;
  const globallySelectedLocationsCount = globallySelectedLocationIds.length;

  return (
    <Modal
      {...modalProps}
      minWidth={600}
      maxWidth={600}
      css={{ boxShadow: theme.shadows.heavy }}
      {...(isModalCloseDisabled && { disableCloseOnEscape: true, disableCloseOnOverlayClick: true })}
    >
      <Modal.Header
        // Hide close button if no locations are stored, this will not allow closing the modal initially with empty selections
        {...(!isModalCloseDisabled && { onClose: modalProps.onClose })}
      >
        {getSelectedLocationsHeader(modalLocationsSelectedCount, globallySelectedLocationsCount)}
      </Modal.Header>
      <Modal.Body css={{ overflowY: 'unset' }}>
        <Text>{getSelectedLocationsDescription(modalLocationsSelectedCount, globallySelectedLocationsCount)}</Text>
        <div css={containerStyle}>
          {selectedLocationIds.map((locationId) => (
            <LocationEntity
              key={locationId}
              maxWidth={130}
              locationName={getScopeName(locationId)}
              onRemove={() => removeLocation(locationId)}
            />
          ))}
          {!isMaxLocationsSelected && !!unselectedLocationIds.length && (
            <AddLocationButton locationIds={unselectedLocationIds} onAdd={addLocation} />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          type: 'submit',
          label: t('View'),
          disabled: modalLocationsSelectedCount === 0,
          onClick: handleViewButtonClick,
        }}
        secondary={{
          label: t('Clear'),
          onClick: () => setSelectedLocationIds([]),
        }}
        helperText={getHelperText(modalLocationsSelectedCount)}
        css={{ '.wv-modal-footer-helper-text': { color: theme.colors.neutral50 } }}
      />
    </Modal>
  );
};

const containerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  gap: theme.spacing(1),
  padding: theme.spacing(3, 0),
});
